/**
 * Enrollment Card
 *
 * A single enrollment (one transaction type) with status badge, payer info, and
 * in-app provider-task completion. Used in the billing setup wizard. The main
 * billing settings tab uses PayerEnrollmentGroup (payer-centric) instead.
 */

import type { Enrollment } from '@features/enrollments';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Avatar, Badge, Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { EnrollmentTaskForm } from './EnrollmentTaskForm';
import {
  getEnrollmentStatusConfig,
  getTransactionTypeLabel,
  isPendingStatus,
} from '../utils/enrollmentStatus';

interface EnrollmentCardProps {
  /** The enrollment to display */
  enrollment: Enrollment;
  /** Whether to show a compact view (for wizard) */
  compact?: boolean;
}

export function EnrollmentCard({ enrollment, compact = false }: EnrollmentCardProps) {
  const theme = useTheme();
  const statusConfig = getEnrollmentStatusConfig(enrollment.status);
  const transactionLabel = getTransactionTypeLabel(enrollment.transaction_type);
  const needsAction = enrollment.status === 'PROVIDER_ACTION_REQUIRED';

  const liveDate = enrollment.live_at
    ? new Date(enrollment.live_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const submittedDate = enrollment.submitted_at
    ? new Date(enrollment.submitted_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  const handleViewInStedi = () => {
    if (enrollment.stedi_enrollment_id) {
      window.open(
        `https://portal.stedi.com/app/healthcare/enrollments/${enrollment.stedi_enrollment_id}`,
        '_blank',
      );
    }
  };

  return (
    <Box
      sx={{
        p: compact ? 1.5 : 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        '&:hover': {
          borderColor: theme.palette.grey[300],
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Payer Avatar */}
        <Avatar
          src={enrollment.payer_avatar_url || undefined}
          alt={enrollment.payer_name || 'Payer'}
          sx={{ width: compact ? 36 : 44, height: compact ? 36 : 44 }}
        >
          {(enrollment.payer_name || 'P').charAt(0)}
        </Avatar>

        {/* Enrollment Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              variant={compact ? 'body2' : 'subtitle2'}
              noWrap
              sx={{ fontWeight: 600, maxWidth: '100%' }}
            >
              {enrollment.payer_name || 'Unknown Payer'}
            </Typography>
            <Chip
              label={statusConfig.label}
              size="small"
              color={statusConfig.color}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {transactionLabel}
            </Typography>

            {enrollment.status === 'LIVE' && liveDate && (
              <>
                <Typography variant="caption" color="text.secondary">
                  •
                </Typography>
                <Typography variant="caption" color="success.main">
                  Active since {liveDate}
                </Typography>
              </>
            )}

            {isPendingStatus(enrollment.status) && submittedDate && (
              <>
                <Typography variant="caption" color="text.secondary">
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Submitted {submittedDate}
                </Typography>
              </>
            )}

            {enrollment.status === 'REJECTED' && enrollment.rejection_reason && (
              <>
                <Typography variant="caption" color="text.secondary">
                  •
                </Typography>
                <Typography variant="caption" color="error.main" noWrap sx={{ maxWidth: 200 }}>
                  {enrollment.rejection_reason}
                </Typography>
              </>
            )}
          </Box>

          {needsAction && enrollment.status_message && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              {enrollment.status_message}
            </Typography>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {enrollment.stedi_enrollment_id && (
            <Tooltip title="View in Stedi dashboard" arrow>
              <Badge
                variant="dot"
                color="warning"
                invisible={!needsAction}
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <IconButton size="small" onClick={handleViewInStedi}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* In-app provider task completion (renders only when a task is open) */}
      <EnrollmentTaskForm enrollment={enrollment} />
    </Box>
  );
}
